// ---------- Text-to-Speech Player ----------
// Dutch-specific version with Google TTS API
// Features: single instance playback, caching, accessibility, error handling

// Shared state across all TTS instances
let currentAudio = null;
let currentBlock = null;
let isDownloading = false;

// Function to initialize a single TTS block
function initTTSBlock(block) {
    // Check if already initialized
    if (block.dataset.ttsInitialized === 'true') return;

    const mainBtn = block.querySelector(".tts-main-btn");
    const expandedSection = block.querySelector(".tts-expanded");
    const playBtn = block.querySelector(".tts-play");
    const pauseBtn = block.querySelector(".tts-pause");
    const stopBtn = block.querySelector(".tts-stop");
    const statusEl = block.querySelector(".tts-status");

    const targetId = block.dataset.targetId;
    const voice = block.dataset.voice;
    const apiKey = tts_vars.apikey;
    const uploadUrl = block.dataset.uploadUrl;

    let filename = block.dataset.filename || null;
    let audioSrc = block.dataset.src || null;
    let isExpanded = false;

    // Find target text
    const targetElement = document.getElementById(targetId);
    if (!targetElement) {
        mainBtn.disabled = true;
        mainBtn.setAttribute('aria-label', `Fout: Element #${targetId} niet gevonden`);
        return;
    }

    const text = targetElement.textContent.trim();
    if (!text) {
        mainBtn.disabled = true;
        mainBtn.setAttribute('aria-label', 'Fout: Geen tekst gevonden om voor te lezen');
        return;
    }

    let audio = null;

    // EXPAND/COLLAPSE FUNCTIONALITY
    function toggleExpanded(expand = null) {
        if (expand === null) {
            isExpanded = !isExpanded;
        } else {
            isExpanded = expand;
        }

        if (isExpanded) {
            block.classList.add('expanded');
            expandedSection.setAttribute('aria-hidden', 'false');
            mainBtn.setAttribute('aria-expanded', 'true');
            mainBtn.setAttribute('aria-label', 'Lees tekst voor - klik om speler te sluiten');
            //    chevron.style.transform = 'rotate(180deg)';
            setTimeout(() => { playBtn.focus(); }, 300);
        } else {
            block.classList.remove('expanded');
            expandedSection.setAttribute('aria-hidden', 'true');
            mainBtn.setAttribute('aria-expanded', 'false');
            mainBtn.setAttribute('aria-label', 'Lees tekst voor - klik om speler te openen');
            //   chevron.style.transform = 'rotate(0deg)';
            if (audio && !audio.paused) { stopAudio(); }
        }
    }

    // WCAG: Update status with proper announcements
    function updateStatus(message) {
        statusEl.textContent = message;
        statusEl.setAttribute('aria-label', message);
    }

    // WCAG: Update button aria-labels dynamically
    function updateButtonLabels(state = 'ready') {
        switch(state) {
            case 'loading':
                playBtn.setAttribute('aria-label', 'Audio wordt geladen...');
                break;
            case 'playing':
                playBtn.setAttribute('aria-label', 'Audio speelt');
                pauseBtn.setAttribute('aria-label', 'Pauzeer audio');
                stopBtn.setAttribute('aria-label', 'Stop audio');
                break;
            case 'paused':
                playBtn.setAttribute('aria-label', 'Hervat audio');
                pauseBtn.setAttribute('aria-label', 'Audio gepauzeerd');
                stopBtn.setAttribute('aria-label', 'Stop audio');
                break;
            case 'ready':
            default:
                playBtn.setAttribute('aria-label', 'Speel tekst af');
                pauseBtn.setAttribute('aria-label', 'Pauzeer audio');
                stopBtn.setAttribute('aria-label', 'Stop audio');
                break;
        }
    }

    function setLoading(msg = "⏳ Laden...") {
        statusEl.innerHTML = `<span class="tts-spinner" aria-hidden="true"></span> ${msg}`;
        playBtn.disabled = true;
        pauseBtn.disabled = true;
        stopBtn.disabled = true;
        updateButtonLabels('loading');
    }

    function resetUI(msg = "⏹️ Gestopt") {
        updateStatus(msg);
        playBtn.disabled = false;
        pauseBtn.disabled = true;
        stopBtn.disabled = true;
        updateButtonLabels('ready');
    }

    function stopAudio() {
        if (audio) {
            audio._stopped = true;
            audio.pause();
            audio.currentTime = 0;

            if (audio.src && audio.src.startsWith('blob:')) {
                try { URL.revokeObjectURL(audio.src); } catch(e) { /* ignore */ }
            }

            resetUI("Audio gestopt");

            if (audio === currentAudio) {
                currentAudio = null;
                currentBlock = null;
            }
        }
    }

    // Helper: stop previous audio
    function stopPreviousAudio(prevAudio, prevBlock, timeout = 800) {
        return new Promise((resolve) => {
            if (!prevAudio) {
                resolve();
                return;
            }

            function cleanupPreviousUI() {
                if (prevBlock) {
                    const s = prevBlock.querySelector(".tts-status");
                    const p = prevBlock.querySelector(".tts-play");
                    const u = prevBlock.querySelector(".tts-pause");
                    const t = prevBlock.querySelector(".tts-stop");
                    if (s) {
                        s.textContent = "Gestopt";
                        s.setAttribute('aria-label', 'Audio gestopt');
                    }
                    if (p) {
                        p.disabled = false;
                        p.setAttribute('aria-label', 'Speel tekst af');
                    }
                    if (u) {
                        u.disabled = true;
                        u.setAttribute('aria-label', 'Pauzeer audio');
                    }
                    if (t) {
                        t.disabled = true;
                        t.setAttribute('aria-label', 'Stop audio');
                    }
                }
            }

            if (prevAudio.paused) {
                cleanupPreviousUI();
                if (prevAudio.src && prevAudio.src.startsWith('blob:')) {
                    try { URL.revokeObjectURL(prevAudio.src); } catch(e){ /* ignore */ }
                }
                if (prevAudio === currentAudio) {
                    currentAudio = null;
                    currentBlock = null;
                }
                resolve();
                return;
            }

            let resolved = false;
            function onPaused() {
                if (resolved) return;
                resolved = true;
                cleanupPreviousUI();
                if (prevAudio.src && prevAudio.src.startsWith('blob:')) {
                    try { URL.revokeObjectURL(prevAudio.src); } catch(e){ /* ignore */ }
                }
                if (prevAudio === currentAudio) {
                    currentAudio = null;
                    currentBlock = null;
                }
                prevAudio._stopped = false;
                prevAudio.removeEventListener('pause', onPaused);
                resolve();
            }

            prevAudio.addEventListener('pause', onPaused, { once: true });
            prevAudio._stopped = true;
            try {
                prevAudio.pause();
                prevAudio.currentTime = 0;
            } catch (e) {
                console.warn("Error stopping previous audio", e);
            }

            setTimeout(() => {
                if (!resolved) {
                    console.warn("Timeout waiting for previous audio to stop");
                    onPaused();
                }
            }, timeout);
        });
    }

    // Base64 → Blob
    function base64ToBlob(base64Data, contentType = 'audio/mp3') {
        const byteCharacters = atob(base64Data);
        const byteNumbers = new Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) {
            byteNumbers[i] = byteCharacters.charCodeAt(i);
        }
        return new Blob([new Uint8Array(byteNumbers)], { type: contentType });
    }

    // Generate filename
    async function generateFilename() {
        if (filename) return filename;
        setLoading("Bestand verwerken...");
        try {
            const response = await fetch(tts_vars.ajaxurl, {
                method: "POST",
                headers: { "Content-Type": "application/x-www-form-urlencoded" },
                body: new URLSearchParams({
                    action: 'generate_tts_filename',
                    text: text,
                    voice: voice,
                    nonce: tts_vars.nonce
                })
            });
            const data = await response.json();
            if (data.success) {
                filename = data.data.filename;
                audioSrc = `${uploadUrl}/${filename}`;
                return filename;
            }
        } catch (err) {
            console.error("generateFilename error", err);
            updateStatus("Initialisatie fout");
        }
        return null;
    }

    // Check cached file
    async function checkCachedFile() {
        if (!audioSrc) return false;
        setLoading("Cache controleren...");
        try {
            const response = await fetch(audioSrc + '?t=' + Date.now(), {
                method: 'HEAD', cache: 'no-cache'
            });
            if (response.ok) {
                audio = new Audio(audioSrc + '?t=' + Date.now());
                setupAudioEvents();
                resetUI("Klaar (uit cache)");
                return true;
            }
        } catch (err) {
            // not found
        }
        return false;
    }

    // Download and create audio
    async function downloadAndCreateAudio() {
        isDownloading = true;
        setLoading("Audio genereren...");

        try {
            if (!filename) await generateFilename();

            const response = await fetch("https://texttospeech.googleapis.com/v1/text:synthesize?key=" + apiKey, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    input: { text: text },
                    voice: { languageCode: "nl-NL", name: voice },
                    audioConfig: { audioEncoding: "MP3" }
                })
            });
            const data = await response.json();
            if (!data.audioContent) {
                console.error("TTS response missing audioContent", data);
                updateStatus("Google TTS fout");
                return;
            }

            const blob = base64ToBlob(data.audioContent);
            const blobUrl = URL.createObjectURL(blob);

            if (audio && audio.src && audio.src.startsWith('blob:') && audio !== currentAudio) {
                try { URL.revokeObjectURL(audio.src); } catch(e){ /* ignore */ }
            }

            audio = new Audio(blobUrl);
            setupAudioEvents();
            updateStatus("▶️ Audio afspelen...");
            updateButtonLabels('playing');
            audio.play();

            // Save in background
            saveFileInBackground(data.audioContent);

        } catch (err) {
            console.error("downloadAndCreateAudio error", err);
            updateStatus("Download fout");
            resetUI("Download fout");
        } finally {
            isDownloading = false;
        }
    }

    // Save file in background
    async function saveFileInBackground(audioContent) {
        if (!filename) await generateFilename();
        if (!filename) return;

        try {
            const resp = await fetch(tts_vars.ajaxurl, {
                method: "POST",
                headers: { "Content-Type": "application/x-www-form-urlencoded" },
                body: new URLSearchParams({
                    action: 'save_tts_file',
                    audio_data: audioContent,
                    filename: filename,
                    nonce: tts_vars.nonce
                })
            });
            const result = await resp.json();
            if (result.success) {
                audioSrc = audioSrc || (uploadUrl + '/' + filename);
                console.log("💾 File opgeslagen:", filename);
            }
        } catch (err) {
            console.warn("⚠️ Opslaan mislukt", err);
        }
    }

    // Setup audio events
    function setupAudioEvents() {
        audio.addEventListener("play", () => {
            currentAudio = audio;
            currentBlock = block;
            updateStatus("Speelt af");
            playBtn.disabled = true;
            pauseBtn.disabled = false;
            stopBtn.disabled = false;
            updateButtonLabels('playing');
        });

        audio.addEventListener("pause", () => {
            if (audio._stopped) {
                audio._stopped = false;
                resetUI("Gestopt");
                if (audio === currentAudio) {
                    currentAudio = null;
                    currentBlock = null;
                }
                return;
            }

            if (audio === currentAudio) {
                if (audio.currentTime < audio.duration) {
                    updateStatus("Gepauzeerd");
                    playBtn.disabled = false;
                    pauseBtn.disabled = true;
                    stopBtn.disabled = false;
                    updateButtonLabels('paused');
                }
            } else {
                resetUI("⏹️ Audio gestopt");
            }
        });

        audio.addEventListener("ended", () => {
            if (audio.src && audio.src.startsWith('blob:')) {
                try { URL.revokeObjectURL(audio.src); } catch(e) { /* ignore */ }
            }
            updateStatus("Audio voltooid");
            resetUI("Audio voltooid");
            if (audio === currentAudio) {
                currentAudio = null;
                currentBlock = null;
            }
        });

        audio.addEventListener("error", (e) => {
            console.error("Audio error", e);
            updateStatus("Audio fout");
            resetUI("Audio fout");
            if (audio === currentAudio) {
                currentAudio = null;
                currentBlock = null;
            }
        });
    }

    // EVENT LISTENERS

    // Main button - toggle expand/collapse
    mainBtn.addEventListener("click", async () => {
        console.log('test')
        if (!isExpanded) {
            // Collapse all other expanded blocks
            document.querySelectorAll(".tts-block.expanded").forEach(otherBlock => {
                if (otherBlock !== block) {
                    otherBlock.classList.remove('expanded');
                    const otherExpanded = otherBlock.querySelector('.tts-expanded');
                    const otherMainBtn = otherBlock.querySelector('.tts-main-btn');
                    if (otherExpanded) otherExpanded.setAttribute('aria-hidden', 'true');
                    if (otherMainBtn) {
                        otherMainBtn.setAttribute('aria-expanded', 'false');
                        otherMainBtn.setAttribute('aria-label', 'Lees tekst voor - klik om speler te openen');
                    }
                }
            });

            toggleExpanded(true);
            // Start playback automatically
            if (isDownloading) {
                updateStatus("Even geduld, audio wordt nog geladen...");
                return;
            }
            if (currentAudio && currentAudio !== audio) {
                setLoading("Audio wisselen...");
                try {
                    await stopPreviousAudio(currentAudio, currentBlock);
                    await new Promise(resolve => setTimeout(resolve, 100));
                } catch (e) {
                    console.warn("Error stopping previous audio", e);
                }
            }
            if (!filename) await generateFilename();
            if (!audio) {
                const exists = await checkCachedFile();
                if (!exists) await downloadAndCreateAudio();
            }
            if (audio) {
                try {
                    audio.currentTime = 0;
                    await audio.play();
                } catch (e) {
                    updateStatus("Kan audio niet afspelen");
                    console.error("Play error:", e);
                }
            }
        } else {
            stopAudio();
            toggleExpanded(false);
            setTimeout(() => { mainBtn.focus(); }, 350);
        }
    });

    playBtn.addEventListener("click", async () => {
        if (isDownloading) {
            updateStatus("Even geduld, audio wordt nog geladen...");
            return;
        }

        if (currentAudio && currentAudio !== audio) {
            setLoading("Audio wisselen...");
            try {
                await stopPreviousAudio(currentAudio, currentBlock);
                await new Promise(resolve => setTimeout(resolve, 100));
            } catch (e) {
                console.warn("Error stopping previous audio", e);
            }
        }

        if (!filename) await generateFilename();

        if (!audio) {
            const exists = await checkCachedFile();
            if (!exists) await downloadAndCreateAudio();
        }

        if (audio) {
            try {
                // Only reset to start if audio is stopped or ended
                if (audio._stopped || audio.currentTime === audio.duration) {
                    audio.currentTime = 0;
                }
                await audio.play();
            } catch (e) {
                updateStatus("Kan audio niet afspelen");
                console.error("Play error:", e);
            }
        }
    });


    // Pause button
    pauseBtn.addEventListener("click", () => {
        if (audio && !audio.paused) {
            audio.pause();
        } else {
            updateStatus("Geen audio om te pauzeren");
        }
    });

    // Stop button
    stopBtn.addEventListener("click", () => {
        stopAudio();
    });



    // Keyboard support
    [playBtn, pauseBtn, stopBtn].forEach(btn => {
        btn.addEventListener('keydown', (e) => {
            if ((e.key === ' ' || e.key === 'Enter') && btn.disabled) {
                e.preventDefault();
                updateStatus("Deze actie is momenteel niet beschikbaar");
            }
        });
    });

    // Initialize
    updateButtonLabels('ready');
    resetUI("Klaar om af te spelen");

    // Mark as initialized
    block.dataset.ttsInitialized = 'true';
}

// Initialize all TTS blocks on page load
document.addEventListener("DOMContentLoaded", () => {
    document.querySelectorAll(".tts-block").forEach(block => {
        initTTSBlock(block);
    });
});

// Make function globally available for dynamic content (e.g., SAM chatbot)
window.initTTSBlock = initTTSBlock;

// Helper function to initialize all uninitialized TTS blocks in a container
window.initTTSBlocks = function(container = document) {
    container.querySelectorAll(".tts-block:not([data-tts-initialized='true'])").forEach(block => {
        initTTSBlock(block);
    });
};

